import React, { useEffect } from "react";
import "./BottomSheet.css";
import { t } from "../i18n";

interface Props {
  open: boolean;
  title: string;
  /** Shown in brand green before the title — the icon of the menu row that opened this sheet. */
  icon?: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
  /** Grab handle only — no title row / ✕ (fan sign-in sheet, locked r6).
      `title` still labels the dialog for screen readers; dismissal is the
      standard scrim tap. */
  plain?: boolean;
  /** Rises to the very top instead of the default 86% (founder pick 1B,
      2026-08-26). Keeps a sliver of scrim above the rounded corners so it
      still reads as a sheet you can flick away — the iOS "large detent"
      look. Used where a fixed-bottom NumberPad would otherwise land on the
      field being typed into (Storefront). */
  tall?: boolean;
  /** May GROW past the default 86% up to the tall cap, but only as far as its
      content needs — Settings must fit one screen without scrolling (build 38). */
  roomy?: boolean;
  /** Extra class on the sheet, for a sheet's own scoped styles. */
  className?: string;
}

// How many sheets are open right now; body.bsheet-open while any is. The fan
// tab bar lives outside <main>, above every sheet drawn inside it, so it steps
// aside under that class (FanTabBar.css) instead of covering a sheet's last
// row (1.5.1: the after-paying sign-up sheet's "Not now").
let openSheets = 0;

// Bottom drawer for the Profile/Balance redesign. Rendered only
// while open — the slide-up runs as a mount animation, so there's no exit
// transition to coordinate. While open, the body is pinned with the same
// position:fixed technique as the Header menu: plain overflow:hidden still
// lets iOS scroll (and rubber-band) the page behind the backdrop.
const BottomSheet: React.FC<Props> = ({ open, title, icon, onClose, children, plain, tall, roomy, className }) => {
  useEffect(() => {
    if (!open) return;
    openSheets += 1;
    document.body.classList.add("bsheet-open");
    return () => {
      openSheets -= 1;
      if (openSheets === 0) document.body.classList.remove("bsheet-open");
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    // Already pinned by the Profile drawer (its sheets open on top of it):
    // pin again and this effect would overwrite the drawer's saved offsets,
    // jumping the frozen page behind everything. One pin is plenty.
    if (document.body.style.position === "fixed") return;
    const scrollY = window.scrollY;
    // Where the pin happened. If the app navigated while this sheet was up
    // (a row inside it pushed a new page), restoring the OLD page's scroll
    // offset onto the NEW page dumps the user mid-screen (founder's "back
    // lands halfway down") — so the offset only restores on the
    // page that was pinned.
    const pinnedPath = window.location.pathname;
    const { style } = document.body;
    const prev = {
      position: style.position,
      top: style.top,
      width: style.width,
      overflow: style.overflow,
    };
    style.position = "fixed";
    style.top = `-${scrollY}px`;
    style.width = "100%";
    style.overflow = "hidden";
    return () => {
      style.position = prev.position;
      style.top = prev.top;
      style.width = prev.width;
      style.overflow = prev.overflow;
      window.scrollTo(0, window.location.pathname === pinnedPath ? scrollY : 0);
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="bsheet-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={`bsheet${tall ? " bsheet--tall" : ""}${roomy ? " bsheet--roomy" : ""}${className ? ` ${className}` : ""}`} role="dialog" aria-modal="true" aria-label={title}>
        <div className={`bsheet-head${plain ? " bsheet-head--plain" : ""}`}>
          <div className="bsheet-grab" aria-hidden="true" />
          {!plain && (
          <div className="bsheet-title-row">
            <span className="bsheet-title">
              {icon && <span className="bsheet-title-ic" aria-hidden="true">{icon}</span>}
              {title}
            </span>
            <button
              type="button"
              className="bsheet-x"
              onClick={onClose}
              aria-label={t("bottomSheet.close")}
            >
              {/* Inline SVG instead of a "✕" text glyph: font glyphs carry
                  baseline/descender space that reads as off-center inside the
                  27px circle (Martin's build-31 test). A stroked path has no
                  font metrics — grid centering puts it dead center on every
                  platform. */}
              <svg viewBox="0 0 12 12" width="11" height="11" aria-hidden="true">
                <path
                  d="M2 2l8 8M10 2l-8 8"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  fill="none"
                />
              </svg>
            </button>
          </div>
          )}
        </div>
        <div className="bsheet-body">{children}</div>
      </div>
    </div>
  );
};

export default BottomSheet;
